import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { SectionHead } from "@/components/Reveal";
import { SERVICE_CATEGORIES } from "@/data/content";

export default function Services() {
    const [cat, setCat] = useState(SERVICE_CATEGORIES[0]);

    return (
        <section id="services" className="relative border-t border-white/10 py-28 md:py-36" data-testid="services-section">
            <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                <SectionHead
                    number="07"
                    label="The service universe"
                    title="Build. Automate. Grow. Protect. Manage."
                    copy="Not a menu of disconnected services — five disciplines engineered to work as one system. Explore each, or book a consultation and we'll map what your business actually needs."
                />
                <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-12">
                    <div className="flex gap-2 overflow-x-auto pb-2 lg:col-span-4 lg:flex-col lg:overflow-visible" data-testid="services-tabs">
                        {SERVICE_CATEGORIES.map((c, i) => (
                            <button
                                key={c.id}
                                data-testid={`services-tab-${c.id}`}
                                onClick={() => setCat(c)}
                                className={`group flex shrink-0 items-center justify-between gap-6 border px-6 py-5 text-left transition-colors duration-300 lg:w-full ${
                                    cat.id === c.id ? "border-white bg-white text-black" : "border-white/12 text-white/60 hover:border-white/40 hover:text-white"
                                }`}
                            >
                                <span className="flex items-center gap-4">
                                    <c.icon size={20} weight="light" />
                                    <span className="font-display text-sm font-bold tracking-[0.25em]">{c.label}</span>
                                </span>
                                <span className="font-mono text-[10px] opacity-50">0{i + 1}</span>
                            </button>
                        ))}
                    </div>
                    <div className="lg:col-span-8">
                        <AnimatePresence mode="wait">
                            <motion.ul
                                key={cat.id}
                                initial={{ opacity: 0, y: 14 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                className="divide-y divide-white/10 border border-white/12 bg-surface"
                                data-testid="services-panel"
                            >
                                {cat.items.map((item, i) => (
                                    <li key={item.label}>
                                        {item.slug ? (
                                            <Link
                                                to={`/services/${item.slug}`}
                                                data-testid={`service-item-${item.slug}`}
                                                className="group flex items-center justify-between px-7 py-6 transition-colors duration-300 hover:bg-white/[0.04]"
                                            >
                                                <span className="flex items-baseline gap-5">
                                                    <span className="font-mono text-[10px] text-white/30">{String(i + 1).padStart(2, "0")}</span>
                                                    <span className="font-display text-xl font-bold tracking-tight text-white md:text-2xl">{item.label}</span>
                                                </span>
                                                <ArrowUpRight size={18} className="text-white/40 transition-all duration-300 group-hover:translate-x-1 group-hover:text-white" />
                                            </Link>
                                        ) : (
                                            <div className="flex items-center justify-between px-7 py-6">
                                                <span className="flex items-baseline gap-5">
                                                    <span className="font-mono text-[10px] text-white/30">{String(i + 1).padStart(2, "0")}</span>
                                                    <span className="font-display text-xl font-bold tracking-tight text-white/70 md:text-2xl">{item.label}</span>
                                                </span>
                                                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/30">Included in every system</span>
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </motion.ul>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    );
}
